/**
 * @file JobTable.jsx
 * @description JobTable component for displaying and managing job table.
 * @component JobTable
 * @exports JobTable - Main component for displaying and managing job table.
 */

import React, { useState } from "react";
import Menu from "@mui/material/Menu";
import Table from "@mui/material/Table";
import Checkbox from "@mui/material/Checkbox";
import TableRow from "@mui/material/TableRow";
import MenuItem from "@mui/material/MenuItem";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import IconButton from "@mui/material/IconButton";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import LinearProgress from "@mui/material/LinearProgress";
import TablePagination from "@mui/material/TablePagination";
import { SIZES, SORT_ORDERS, ALIGN, HTML_TAGS } from "../../../common/constants/commonConstants.js";
import { JOB_TABLE_COLUMN_KEYS, JOB_MESSAGES } from "../../../common/constants/ivrModule/jobConstants.js";

const JobTable = ({
    page,
    order,
    setPage,
    orderBy,
    jobs,
    rowsPerPage,
    selectedIds,
    isJobLoading,
    columns,
    statusFilter,
    statusOptions,
    setRowsPerPage,
    onSelectionChange,
    filteredJobLength,
    handleRequestSort,
    onStatusFilterChange,
}) => {

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const isSelected = (id) => selectedIds.includes(id);
    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const allIds = jobs.map((j) => j.id);
            onSelectionChange([...new Set([...selectedIds, ...allIds])]);
        } else {
            const pageIds = jobs.map((j) => j.id);
            onSelectionChange(selectedIds.filter((id) => !pageIds.includes(id)));
        }
    };
    const handleClick = (id) => {
        if (isSelected(id)) {
            onSelectionChange(selectedIds.filter((sid) => sid !== id));
        } else {
            onSelectionChange([...selectedIds, id]);
        }
    };
    const handleFilterSelect = (status) => {
        onStatusFilterChange(status);
        handleMenuClose();
    };

    return (
        <TableContainer sx={{ mt: 2 }}>
            <Table size={SIZES.SMALL}>
                <TableHead>
                    <TableRow>
                        {columns.map((column) => (
                            <TableCell
                                key={column.id}
                                sortDirection={orderBy === column.id ? order : false}
                                align={column.id === JOB_TABLE_COLUMN_KEYS.SELECT ? ALIGN.CENTER : ALIGN.LEFT}
                                sx={{
                                    width:
                                        column.id === JOB_TABLE_COLUMN_KEYS.SELECT ? 50 :
                                        column.id === JOB_TABLE_COLUMN_KEYS.ID ? 200 :
                                        column.id === JOB_TABLE_COLUMN_KEYS.STATUS ? 150 :
                                        column.id === JOB_TABLE_COLUMN_KEYS.QUEUE_TIME ? 200 :
                                        column.id === JOB_TABLE_COLUMN_KEYS.QUEUE_ID ? 200 :
                                        column.id === JOB_TABLE_COLUMN_KEYS.PRIORITY ? 200 :
                                        column.id === JOB_TABLE_COLUMN_KEYS.SKILL ? 150 :
                                        column.id === JOB_TABLE_COLUMN_KEYS.OPERATOR ? 150 :
                                        column.id === JOB_TABLE_COLUMN_KEYS.SKILL_VALUE ? 100 :
                                        column.id === JOB_TABLE_COLUMN_KEYS.WORKER_ID ? 200 :
                                        column.id === JOB_TABLE_COLUMN_KEYS.ASSIGNED_TIME ? 250 : 200,
                                }}
                            >
                                {column.id === JOB_TABLE_COLUMN_KEYS.SELECT ? (
                                    <Checkbox
                                        indeterminate={
                                            jobs.some((j) => selectedIds.includes(j.id)) &&
                                            !jobs.every((j) => selectedIds.includes(j.id))
                                        }
                                        checked={
                                            jobs.length > 0 &&
                                            jobs.every((j) => selectedIds.includes(j.id))
                                        }
                                        onChange={handleSelectAllClick}
                                    />
                                ) : column.id === JOB_TABLE_COLUMN_KEYS.STATUS ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : SORT_ORDERS.ASCENDING}
                                            onClick={() => handleRequestSort(column.id)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                        <IconButton size="small" onClick={handleMenuOpen}>
                                            <FilterAltIcon fontSize="small" sx={{ color: statusFilter ? "blue" : "grey" }} />
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={open}
                                            onClose={handleMenuClose}
                                        >
                                            <MenuItem
                                                selected={!statusFilter}
                                                onClick={() => handleFilterSelect("")}
                                            >
                                                All
                                            </MenuItem>
                                            {statusOptions.map((status) => (
                                                <MenuItem
                                                    key={status}
                                                    selected={statusFilter === status}
                                                    onClick={() => handleFilterSelect(status)}
                                                >
                                                    {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </div>
                                ) : column.isSortable ? (
                                    <TableSortLabel
                                        active={orderBy === column.id}
                                        direction={orderBy === column.id ? order : SORT_ORDERS.ASCENDING}
                                        onClick={() => handleRequestSort(column.id)}
                                    >
                                        {column.label}
                                    </TableSortLabel>
                                ) : column.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {isJobLoading ? (
                        <TableRow>
                            <TableCell colSpan={columns.length} align={ALIGN.CENTER}>
                                <LinearProgress />
                            </TableCell>
                        </TableRow>
                    ) : jobs.length > 0 ? jobs.map((job) => {
                        const isItemSelected = isSelected(job.id);
                        return (
                            <TableRow
                                hover
                                role="checkbox"
                                tabIndex={-1}
                                key={job.id}
                                selected={isItemSelected}
                            >
                                <TableCell align={ALIGN.CENTER}>
                                    <Checkbox
                                        checked={isItemSelected}
                                        onChange={() => handleClick(job.id)}
                                    />
                                </TableCell>
                                <TableCell>{job.id}</TableCell>
                                <TableCell>{job.status.charAt(0).toUpperCase() + job.status.slice(1).toLowerCase()}</TableCell>
                                <TableCell>
                                    {new Date(job.enqueuedAt).toLocaleDateString('en-US')},{" "}
                                    {new Date(job.enqueuedAt).toLocaleTimeString('en-US', {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                    })}
                                </TableCell>
                                <TableCell>{job.queueId}</TableCell>
                                <TableCell>{job.priority}</TableCell>
                                <TableCell>{job.skill}</TableCell>
                                <TableCell>{job.operator}</TableCell>
                                <TableCell>{job.skillValue}</TableCell>
                                <TableCell>{job.workerId}</TableCell>
                                <TableCell>
                                    {new Date(job.assignedAt).toLocaleDateString('en-US')},{" "}
                                    {new Date(job.assignedAt).toLocaleTimeString('en-US', {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                    })}
                                </TableCell>
                            </TableRow>
                        );
                    }) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} align={ALIGN.CENTER}>
                                {JOB_MESSAGES.NO_JOB}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            <TablePagination
                page={page}
                rowsPerPage={rowsPerPage}
                component={HTML_TAGS.DIV}
                count={filteredJobLength}
                rowsPerPageOptions={[10, 25, 50]}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                    setPage(0);
                    setRowsPerPage(parseInt(event.target.value, 10));
                }}
            />
        </TableContainer>
    );
};

export default JobTable;